import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { EERIE, GYPSUM, HEFFALUMP, KOALA, OLAF } from 'HubStyleTokens/colors';
import { WEB_FONT_DEMI_BOLD_WEIGHT, WEB_FONT_REGULAR_WEIGHT } from 'HubStyleTokens/misc';
import PropTypes from 'prop-types';
import devLogger from 'react-utils/devLogger';
import styled, { css } from 'styled-components';
import UICloseButton from '../button/UICloseButton';
import Small from '../elements/Small';
import UIIcon from '../icon/UIIcon';
import UIMedia from '../layout/UIMedia';
import UIMediaBody from '../layout/UIMediaBody';
import UIMediaLeft from '../layout/UIMediaLeft';
import UITruncateString from '../text/UITruncateString';
import * as customRenderer from '../utils/propTypes/customRenderer';
import { propTypeForSizes, toShorthandSize } from '../utils/propTypes/tshirtSize';
import { setBorderRadius, setFontSize } from '../utils/Styles';
const SIZE_VALUES = {
  fontSize: '12px',
  sm: {
    iconFont: '16px',
    previewWidth: '16px'
  },
  lg: {
    iconFont: '32px',
    previewWidth: '32px'
  }
};

const DefaultFilePreview = ({
  fileType
}) => /*#__PURE__*/_jsx(UIIcon, {
  className: "align-self-end",
  name: fileType === 'application/pdf' ? 'pdfFile' : 'file'
});

DefaultFilePreview.propTypes = {
  fileType: PropTypes.string
};
const defaultProps = {
  closeable: false,
  processing: false,
  use: 'default',
  size: 'lg',
  renderPreview: DefaultFilePreview
};
const StyledAttachment_Children = styled.div.withConfig({
  displayName: "UIFileAttachment__StyledAttachment_Children",
  componentId: "sc-1blqn35-0"
})(["margin:0;bottom:0;left:0;position:absolute;right:0;top:0;"]);
const StyledAttachment_Media = styled(UIMedia).withConfig({
  displayName: "UIFileAttachment__StyledAttachment_Media",
  componentId: "sc-1blqn35-1"
})(["position:relative;max-width:100%;min-width:0;line-height:", ";"], ({
  size
}) => size === 'sm' && SIZE_VALUES.fontSize);
const StyledAttachment_Preview = styled(UIMediaLeft).withConfig({
  displayName: "UIFileAttachment__StyledAttachment_Preview",
  componentId: "sc-1blqn35-2"
})(["align-items:center;display:flex;flex-basis:auto;flex-shrink:0;", ";"], ({
  size = defaultProps['size']
}) => css(["", " max-height:", ";width:", ";> *{max-height:", ";max-width:", ";}"], setFontSize(SIZE_VALUES[size].iconFont), SIZE_VALUES[size].previewWidth, SIZE_VALUES[size].previewWidth, SIZE_VALUES[size].previewWidth, SIZE_VALUES[size].previewWidth));
StyledAttachment_Preview.displayName = 'StyledAttachment_Preview';
const StyledAttachment_Info = styled(UIMediaBody).withConfig({
  displayName: "UIFileAttachment__StyledAttachment_Info",
  componentId: "sc-1blqn35-3"
})(["display:flex;flex-direction:column;justify-content:center;line-height:1rem;width:100%;&&{flex-basis:auto;margin-left:", ";}", ";"], ({
  size
}) => size === 'sm' && '8px', ({
  size
}) => {
  if (size === 'sm') return css(["align-items:center;flex-direction:row;white-space:nowrap;"]);else if (size === 'lg') return css(["min-height:2rem;"]);
  return null;
});
const FileInfo = styled(({
  className,
  fileInfo,
  size
}) => {
  return fileInfo ? /*#__PURE__*/_jsxs(Small, {
    className: className,
    use: "help",
    children: [size === 'sm' && '(', fileInfo, size === 'sm' && ')']
  }) : null;
}).withConfig({
  displayName: "UIFileAttachment__FileInfo",
  componentId: "sc-1blqn35-4"
})(["&&{font-weight:", ";line-height:inherit;margin-left:", ";}"], WEB_FONT_REGULAR_WEIGHT, ({
  size
}) => size === 'sm' && '1ch');
const StyledAttachment_Close = styled((_ref) => {
  let rest = _objectWithoutPropertiesLoose(_ref, ["attachmentSize"]);

  return /*#__PURE__*/_jsx(UICloseButton, Object.assign({}, rest));
}).withConfig({
  displayName: "UIFileAttachment__StyledAttachment_Close",
  componentId: "sc-1blqn35-5"
})(["flex-shrink:0;color:", ";right:10px;top:10px;vertical-align:text-top;", ";"], EERIE, ({
  attachmentSize
}) => attachmentSize === 'sm' && css(["align-self:center;margin-left:auto;margin-right:0;position:static;margin-left:8px;&::after{left:auto;margin-left:12px;}"]));
StyledAttachment_Close.displayName = 'StyledAttachment_Close';
const StyledAttachment = styled.div.withConfig({
  displayName: "UIFileAttachment__StyledAttachment",
  componentId: "sc-1blqn35-6"
})(["display:", ";padding:", ";", ";", ";background-color:", ";border:1px solid ", ";color:", ";font-weight:", ";max-width:100%;position:relative;justify-content:space-between;padding-right:", ";"], ({
  size
}) => size === 'sm' ? 'flex' : 'inline-flex', ({
  size
}) => size === 'sm' ? '8px 12px' : '12px', setBorderRadius(), setFontSize(SIZE_VALUES.fontSize), ({
  use
}) => use === 'on-dark' ? OLAF : GYPSUM, KOALA, ({
  processing
}) => processing ? EERIE : HEFFALUMP, WEB_FONT_DEMI_BOLD_WEIGHT, ({
  size
}) => size === 'lg' && '32px');
export default function UIFileAttachment(props) {
  const {
    closeable,
    use,
    size,
    children,
    renderPreview,
    fileName,
    fileInfo,
    onCloseClick,
    processing
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["closeable", "use", "size", "children", "renderPreview", "fileName", "fileInfo", "fileType", "onCloseClick", "processing"]);

  const shorthandSize = size ? toShorthandSize(size) : toShorthandSize(defaultProps['size']);
  const renderedPreview = customRenderer.render(renderPreview, props, {
    mergeType: 'none'
  });

  const renderedFileInfo = /*#__PURE__*/_jsx(FileInfo, {
    size: shorthandSize,
    fileInfo: fileInfo
  });

  if (use === 'processing') {
    devLogger.warn({
      message: 'UIFileAttachment: `use="processing"` is deprecated. Set `processing={true}` instead.',
      key: 'UIFileAttachment: processing',
      url: 'https://git.hubteam.com/HubSpot/UIComponents/issues/7991#issuecomment-1176756'
    });
  }

  return /*#__PURE__*/_jsxs(StyledAttachment, Object.assign({}, rest, {
    processing: processing || use === 'processing',
    size: shorthandSize,
    use: use,
    children: [children && /*#__PURE__*/_jsx(StyledAttachment_Children, {
      size: shorthandSize,
      children: children
    }), /*#__PURE__*/_jsxs(StyledAttachment_Media, {
      size: shorthandSize,
      children: [/*#__PURE__*/_jsx(StyledAttachment_Preview, {
        size: shorthandSize,
        children: renderedPreview
      }), /*#__PURE__*/_jsxs(StyledAttachment_Info, {
        size: shorthandSize,
        children: [shorthandSize === 'sm' ? /*#__PURE__*/_jsx(UITruncateString, {
          useFlex: true,
          fixedChildren: renderedFileInfo,
          children: fileName
        }) : /*#__PURE__*/_jsx(UITruncateString, {
          useFlex: true,
          children: fileName
        }), shorthandSize !== 'sm' && renderedFileInfo]
      })]
    }), closeable && /*#__PURE__*/_jsx(StyledAttachment_Close, {
      className: "private-attachment__close",
      onClick: onCloseClick,
      size: "sm",
      attachmentSize: shorthandSize
    })]
  }));
}
UIFileAttachment.propTypes = {
  children: PropTypes.node,
  closeable: PropTypes.bool.isRequired,
  onCloseClick: PropTypes.func,
  processing: PropTypes.bool.isRequired,
  use: PropTypes.oneOf(['default', 'on-dark', 'processing']),
  size: PropTypes.oneOfType([propTypeForSizes(['sm', 'lg'])]).isRequired,
  fileName: PropTypes.node.isRequired,
  fileInfo: PropTypes.node,
  fileType: PropTypes.string,
  renderPreview: customRenderer.propType
};
UIFileAttachment.defaultProps = defaultProps;
UIFileAttachment.displayName = 'UIFileAttachment';